"use client";

import { useEffect } from "react";
import { Link2 } from "lucide-react";
import { HOW_TO_SECTIONS } from "@/lib/howToGuides.mjs";

function BookmarkHeading({ as: Heading, id, children, className }) {
  return (
    <Heading id={id} className={`scroll-mt-28 ${className}`}>
      <a
        href={`#${id}`}
        className="group inline-flex max-w-full items-center gap-2 text-inherit no-underline"
        aria-label={`Link to ${children}`}
      >
        <span>{children}</span>
        <Link2
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-[var(--muted)] opacity-70 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        />
      </a>
    </Heading>
  );
}

export default function HowToContent() {
  useEffect(() => {
    const bookmark = window.location.hash.slice(1);
    if (!bookmark) return undefined;

    let id = bookmark;
    try {
      id = decodeURIComponent(bookmark);
    } catch {
      id = bookmark;
    }

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="edge-mobile">
      <article className="card square-mobile space-y-8">
        <header className="space-y-2">
          <h2 className="h2">How To</h2>
          <p className="max-w-2xl text-sm leading-relaxed text-[var(--muted)]">
            Find straightforward guides for setting up Sproutly, participating in spaces,
            and managing your account.
          </p>
        </header>

        <nav aria-label="How To topics" className="border-y border-[var(--border)] py-4">
          <p className="mb-3 text-xs font-semibold uppercase text-[var(--muted)]">Browse topics</p>
          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {HOW_TO_SECTIONS.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-sm font-semibold text-[var(--brand)] underline-offset-4 hover:underline"
              >
                {section.title}
              </a>
            ))}
          </div>
        </nav>

        <div className="divide-y divide-[var(--border)]">
          {HOW_TO_SECTIONS.map((section) => (
            <section
              key={section.id}
              aria-labelledby={section.id}
              className="py-7 first:pt-0 last:pb-0"
            >
              <div className="border-l-4 border-[var(--brand)] pl-4">
                <BookmarkHeading
                  as="h2"
                  id={section.id}
                  className="text-xl font-semibold text-[var(--brand)]"
                >
                  {section.title}
                </BookmarkHeading>
                <p className="mt-1 text-sm leading-relaxed text-[var(--muted)]">
                  {section.description}
                </p>
                {section.callout ? (
                  <p className="mt-3 max-w-3xl rounded-[var(--radius-sm)] bg-[var(--brand-weak)] px-3 py-2 text-sm font-medium leading-relaxed text-[var(--fg-primary)]">
                    {section.callout}
                  </p>
                ) : null}
              </div>

              <div className="ml-2 mt-4 divide-y divide-[var(--border)] border-l border-[var(--border)] pl-4 sm:ml-4 sm:pl-6">
                {section.guides.map((guide) => (
                  <section key={guide.id} aria-labelledby={guide.id} className="py-4 last:pb-0">
                    <BookmarkHeading
                      as="h3"
                      id={guide.id}
                      className="text-base font-medium text-[var(--fg-primary)]"
                    >
                      {guide.title}
                    </BookmarkHeading>
                    <p className="mt-1 max-w-3xl text-sm leading-relaxed text-[var(--muted)]">
                      {guide.description}
                    </p>
                    <ol className="mt-3 max-w-3xl space-y-2 text-sm leading-relaxed text-[var(--fg-secondary)]">
                      {guide.steps.map((step, index) => (
                        <li key={step} className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2">
                          <span
                            aria-hidden="true"
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--surface-2)] text-xs font-semibold text-[var(--brand)]"
                          >
                            {index + 1}
                          </span>
                          <span className="pt-0.5">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </section>
                ))}
              </div>
            </section>
          ))}
        </div>
      </article>
    </div>
  );
}
